// Duplicate SIN / Business Number check (`GET api/customers/sin/business-number`).
// The server answers 409 for a number another client of the firm already has
// (QA batch 8; older servers answered 500 with "... already exists"). Any
// other failure means the check itself didn't work.

export type NumberCheckResult = "duplicate" | "failed";

export const numberCheckResult = (error: unknown): NumberCheckResult => {
  const e = error as { status?: unknown; data?: unknown } | null;
  if (e?.status === 409) return "duplicate";
  const data =
    typeof e?.data === "string" ? e.data : JSON.stringify(e?.data ?? "");
  if (e?.status === 500 && /already exists/i.test(data)) return "duplicate";
  return "failed";
};

export const NUMBER_CHECK_FAILED =
  "We couldn't check this number right now. Please try again.";
export const DUPLICATE_SIN = "Another client already has this SIN.";
export const DUPLICATE_BUSINESS_NUMBER =
  "Another client already has this business number.";
/** The same messages with the firm country's name for the number
 * (SSN / EIN / ABN...; `region.ts`). Canada keeps the texts above. */
export const duplicatePersonalIdText = (label: string) =>
  /^SIN/i.test(label)
    ? DUPLICATE_SIN
    : `Another client already has this ${label}.`;
export const duplicateBusinessIdText = (label: string) =>
  /^Business Number$/i.test(label)
    ? DUPLICATE_BUSINESS_NUMBER
    : `Another client already has this ${label}.`;

/** Business Number box: the 9-digit BN (program accounts such as RT0001 go
 * in GST/HST Number). Pasting "123456789 RT0001" keeps the first 9 digits. */
export const BUSINESS_NUMBER_DIGITS = 9;
export const toBusinessNumber = (text: string) =>
  (text || "").replace(/\D/g, "").slice(0, BUSINESS_NUMBER_DIGITS);

/** QA batch 12 (2026-10-21): the box is empty or exactly 9 digits (the
 * server checks the same). `saved` = an older stored value, accepted unchanged. */
export const BUSINESS_NUMBER_RULE = "Business number must be 9 digits";
export const businessNumberProblem = (
  text?: string | null,
  saved?: string | null,
): string => {
  const value = (text || "").trim();
  if (!value || /^\d{9}$/.test(value)) return "";
  if (saved != null && value === (saved || "").trim()) return "";
  return BUSINESS_NUMBER_RULE;
};

/** Only a complete 9-digit BN is checked for duplicates (QA batch 11: an
 * unfinished "12345678" was reported as already used). */
export const shouldCheckBusinessNumber = (text: string) =>
  /^\d{9}$/.test((text || "").trim());

/** Keys the digits-only box lets through besides digits: editing keys and any
 * Ctrl/Cmd shortcut (QA batch 11: Ctrl+A was blocked, so Ctrl+A then
 * Backspace removed only one digit). */
export const isAllowedNumberBoxKey = (event: {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
}) =>
  /^[0-9]$/.test(event.key) ||
  !!event.ctrlKey ||
  !!event.metaKey ||
  [
    "Backspace",
    "Delete",
    "Tab",
    "Enter",
    "Escape",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ].includes(event.key);
